/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { useEffect, useRef, useState } from 'react';

import { useGlobalAppData, useGlobalServices } from '../../components/providers/GlobalContextProvider';
import { generateGuid } from '../../helpers/string-helpers';
import { NotificationDialogsTextKeys } from '../../services/notification';
import { BeforeLeave } from '../../helpers/before-leave';
import { usePopstate } from '../use-popstate';

interface UseNavigationBlockerProps {
    isDirty: boolean;
    prompt: {
        title: string;
        message: string;
    };
    requestClose?: () => void;
}

export const useNavigationBlocker = ({ prompt: { title, message }, isDirty, requestClose }: UseNavigationBlockerProps) => {
    const beforeLeave = BeforeLeave.getInstance();
    const globalAppData = useGlobalAppData();
    const { notificationService } = useGlobalServices();
    const fakeRoute = useRef(generateGuid());
    const [staticData] = useState({
        isDirty,
    });
    staticData.isDirty = isDirty;

    const closeAction = () => {
        if (requestClose) {
            requestClose();
        } else {
            window.history.back();
        }
    };

    const { activate, deactivate } = usePopstate(
        () => {
            if (!staticData.isDirty) {
                closeAction();
                return;
            }

            if (staticData.isDirty) {
                notificationService.confirm(globalAppData.navigationBlockerPrompt.title, globalAppData.navigationBlockerPrompt.message)
                    .then((btn) => {
                        if (btn === NotificationDialogsTextKeys.Yes) {
                            beforeLeave.hide(fakeRoute.current);
                            globalAppData.navigationBlockerPrompt = null;
                            closeAction();
                        } else {
                            activate();
                        }
                    });
            }
        },
        false,
    );

    useEffect(() => {
        if (isDirty) {
            globalAppData.navigationBlockerPrompt = { title, message };
            beforeLeave.show(fakeRoute.current);
        } else {
            beforeLeave.hide(fakeRoute.current);
            globalAppData.navigationBlockerPrompt = null;
        }
    }, [isDirty]);

    useEffect(() => () => {
        beforeLeave.hide(fakeRoute.current);
        globalAppData.navigationBlockerPrompt = null;
    }, []);

    useEffect(() => {
        activate();
    }, []);

    const cleanup = () => {
        deactivate();
        globalAppData.navigationBlockerPrompt = null;
        beforeLeave.hide(fakeRoute.current);
    };

    return {
        cleanup,
    };
};
